/**
 * PURPOSE: Reglas puras de opciones de uniforme en Winscore (FTR-0014; modelo original restaurado
 *   2026-09-14: hasta 3 opciones ordenadas por tipo y tono, con foto por opción; corrección de etiqueta
 *   2026-09-14: el nombre de color es la etiqueta principal, el orden es metadato secundario).
 * RESPONSIBILITIES: `primaryOptionLabel`/`contextualOptionLabel` son la única fuente de la etiqueta visible
 *   de una opción — nunca se reconstruye el texto de forma distinta en cada componente. Nombre de tipo/tono
 *   ("Camiseta clara", "Arquero oscuro", "Líbero claro"...) solo para encabezados de sección
 *   ("CAMISETAS CLARAS"), botón de alta contextual ("+ Agregar camiseta clara" / "+ Agregar otra camiseta
 *   clara") y contexto cuando el tipo/tono no es evidente por sí solo (Programación); nunca como etiqueta
 *   principal de una opción ni dentro de la etiqueta guardada, que sigue siendo "Predominante" o
 *   "Predominante con Complementario" (el orden en que el delegado elige predominante/complementario nunca
 *   se invierte). Textos del plazo (fail closed) y del estado del equipo; hora de Lima (America/Lima, UTC−5
 *   sin horario de verano) para confirmar fechas pasadas; payloads de alta y edición (solo cambios) y de
 *   reordenamiento (nunca cambia la etiqueta de una opción, solo su orden); mensajes por código de error;
 *   asignaciones por partido cambiadas. Corrección de usabilidad 2026-09-14: `uniformVisual` es la única
 *   fuente de la presentación “foto Y swatches” (la foto nunca reemplaza los colores) y
 *   `complementaryStartsOpen`/`clearComplementary`/`canSubmitOptionForm` fijan que el color predominante es
 *   el único obligatorio y que el complementario es opcional y colapsado.
 * COLLABORATORS: CompanyUniformsTab.jsx, UniformOptionEditor.jsx, UniformOptionVisual.jsx,
 *   UniformDeadlinePanel.jsx, MatchUniformOptionsFields.jsx, auditDisplay.utils.js.
 * ROUTE: winscore/src/admin/AdminCompany/uniformOptions.utils.js
 */
export const UNIFORM_TONES = Object.freeze({ LIGHT: 1, DARK: 2 });
export const UNIFORM_TONE_LABELS = Object.freeze({ 1: "Clara", 2: "Oscura" });
export const UNIFORM_TYPE_LABELS = Object.freeze({ 1: "Camiseta", 2: "Arquero", 3: "Líbero" });
export const MAX_OPTIONS_PER_TYPE_TONE = 3;
// Adjetivo de tono con la concordancia de género de cada tipo (igual que el backend): "Camiseta clara/oscura"
// es femenino; "Arquero"/"Líbero" claro/oscuro son masculinos.
const TONE_ADJECTIVES_BY_TYPE = Object.freeze({
  1: Object.freeze({ 1: "clara", 2: "oscura" }),
  2: Object.freeze({ 1: "claro", 2: "oscuro" }),
  3: Object.freeze({ 1: "claro", 2: "oscuro" }),
});
export const slotName = (column) => {
  const typeName = column?.typeName || UNIFORM_TYPE_LABELS[column?.idtype] || "Uniforme";
  const adjective = TONE_ADJECTIVES_BY_TYPE[column?.idtype]?.[column?.tone] || "";
  return [typeName, adjective].filter(Boolean).join(" ");
};
/** "Camiseta clara 2", "Arquero oscuro 1"... solo para distinguir alternativas del mismo tipo/tono en la UI. */
export const numberedOptionLabel = (column, order) => `${slotName(column)} ${order}`.trim();
/** Metadato secundario de posición ("Opción 1", "Opción 2"...); nunca la etiqueta principal de una opción. */
export const optionPositionLabel = (order) => (order ? `Opción ${order}` : "");
/** Encabezado de sección ("CAMISETAS CLARAS", "UNIFORMES DE ARQUERO CLARO"...). */
export const sectionLabel = (column) => {
  if (Number(column?.idtype) === 1) return `Camisetas ${column?.tone === UNIFORM_TONES.DARK ? "oscuras" : "claras"}`.toUpperCase();
  return `Uniformes de ${slotName(column)}`.toUpperCase();
};
/**
 * Texto contextual del botón de alta ("+ Agregar camiseta clara" / "+ Agregar otra camiseta clara";
 * "+ Agregar uniforme de arquero claro" / "+ Agregar otro uniforme de arquero claro"...), con la
 * concordancia de género de cada tipo.
 */
export const addOptionButtonLabel = (column, hasAny) => {
  const adjective = TONE_ADJECTIVES_BY_TYPE[column?.idtype]?.[column?.tone] || "";
  if (Number(column?.idtype) === 1) {
    return hasAny ? `+ Agregar otra camiseta ${adjective}` : `+ Agregar camiseta ${adjective}`;
  }
  const typeName = (column?.typeName || UNIFORM_TYPE_LABELS[column?.idtype] || "uniforme").toLowerCase();
  return hasAny ? `+ Agregar otro uniforme de ${typeName} ${adjective}` : `+ Agregar uniforme de ${typeName} ${adjective}`;
};
export const DESCRIPTION_MAX_LENGTH = 64;
export const LIMA_TIME_ZONE = "America/Lima";
const LIMA_UTC_OFFSET = "-05:00";

// Nombres del catálogo aprobado por clave (solo para mostrar auditoría; la fuente es el backend).
export const UNIFORM_COLOR_NAMES = Object.freeze({
  blanco: "Blanco", negro: "Negro", azul: "Azul", azul_oscuro: "Azul Oscuro", azul_marino: "Azul Marino",
  azulino: "Azulino", celeste: "Celeste", turquesa: "Turquesa", verde: "Verde", verde_claro: "Verde Claro",
  amarillo: "Amarillo", naranja: "Naranja", anaranjado: "Anaranjado", rojo: "Rojo", rojo_claro: "Rojo Claro",
  guinda: "Guinda", granate: "Granate", rosado: "Rosado", fucsia: "Fucsia", morado: "Morado", purpura: "Púrpura",
  violeta: "Violeta", lila: "Lila", gris: "Gris", plomo: "Plomo",
});

export const columnLabel = (column) =>
  `${column?.typeName || UNIFORM_TYPE_LABELS[column?.idtype] || "Uniforme"} · ${UNIFORM_TONE_LABELS[column?.tone] || ""}`.trim();

/** "Predominante" o "Predominante con Complementario"; igual que el backend (el tipo/tono/orden se muestran
 * aparte en la UI, nunca dentro de la etiqueta guardada — así dos opciones del mismo tipo y tono no se
 * confunden por compartir el mismo color predominante: se distinguen por su número de orden). */
export const buildUniformLabel = (primary, secondary) => {
  if (!primary?.name) return "";
  return secondary?.name ? `${primary.name} con ${secondary.name}` : primary.name;
};

const FALLBACK_OPTION_LABEL = "Color sin definir";

/**
 * Única fuente de la etiqueta PRINCIPAL visible de una opción — el nombre de color, nunca el número de
 * orden. Usa la etiqueta ya generada por el backend (`option.label`) y, si faltara, la reconstruye desde
 * las referencias de color vigentes con `buildUniformLabel` (así una edición de color se refleja de
 * inmediato en todo lugar que use esta función). Si el color de una opción legacy no se puede resolver,
 * devuelve un texto seguro en vez de "undefined"/"null"/vacío.
 */
export const primaryOptionLabel = (option) => {
  const stored = typeof option?.label === "string" ? option.label.trim() : "";
  if (stored) return stored;
  return buildUniformLabel(option?.primaryColor, option?.secondaryColor) || FALLBACK_OPTION_LABEL;
};

/**
 * Etiqueta contextual para usarse donde el tipo/tono de una opción NO es evidente por el contexto que la
 * rodea (p. ej. un selector de Programación donde conviven alternativas de distintos tipos y tonos):
 * "Camiseta clara — Celeste con Blanco". Donde la sección ya establece el tipo/tono (p. ej. bajo un
 * encabezado "CAMISETAS CLARAS"), usar solo `primaryOptionLabel`. Acepta una `column` (matriz de admin) o,
 * si se omite, resuelve el tipo/tono desde la propia opción (`option.type`/`option.tone`, forma del
 * contrato de Programación).
 */
export const contextualOptionLabel = (option, column) => {
  const label = primaryOptionLabel(option);
  const context = column || { idtype: option?.type?.id, typeName: option?.type?.name, tone: option?.tone?.id };
  const name = slotName(context);
  return name ? `${name} — ${label}` : label;
};

export const normalizeDescription = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

/** Devuelve el mensaje de error o null; misma regla que el backend (solo letras y espacios, sin HEX). */
export const validateDescription = (value) => {
  const text = normalizeDescription(value);
  if (!text) return null;
  if ([...text].length > DESCRIPTION_MAX_LENGTH) return `La descripción no puede superar ${DESCRIPTION_MAX_LENGTH} caracteres.`;
  if (!/^\p{L}+( \p{L}+)*$/u.test(text) || text.split(" ").some((word) => /^[0-9a-f]{6}$/i.test(word))) {
    return "Solo letras y espacios (sin números ni códigos de color).";
  }
  return null;
};

/** Valida el formulario del editor; devuelve { field: mensaje }. */
export const validateOptionForm = (form) => {
  const errors = {};
  if (!form.primaryColorId) errors.primaryColorId = "Elige el color predominante.";
  if (form.secondaryColorId && Number(form.secondaryColorId) === Number(form.primaryColorId)) {
    errors.secondaryColorId = "El complementario debe ser distinto del predominante.";
  }
  return errors;
};

const toIdOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));

export const buildCreatePayload = ({ idtype, tone, form, replaceLegacyIduniform = null }) => ({
  idtype: Number(idtype),
  tone: Number(tone),
  primaryColorId: Number(form.primaryColorId),
  secondaryColorId: toIdOrNull(form.secondaryColorId),
  ...(replaceLegacyIduniform ? { replaceLegacyIduniform: Number(replaceLegacyIduniform) } : {}),
});

/** Solo los campos cambiados respecto de la opción actual (PATCH); el tono/tipo del slot ya no es editable. */
export const buildUpdatePayload = (option, form) => {
  const payload = {};
  if (Number(form.primaryColorId) !== Number(option.primaryColor?.id)) payload.primaryColorId = Number(form.primaryColorId);
  const secondary = toIdOrNull(form.secondaryColorId);
  if (secondary !== (option.secondaryColor?.id ?? null)) payload.secondaryColorId = secondary;
  return payload;
};

/**
 * Presentación de una opción: la foto ACOMPAÑA a los swatches, nunca los reemplaza (corrección
 * 2026-09-14). Devuelve siempre los colores en el orden del dominio (predominante primero, complementario
 * después) y, de forma aditiva, la foto cuando existe. `photoFailed` (imagen rota) retira únicamente la
 * foto: los colores y la etiqueta siguen visibles. Acepta la forma de administración/delegado
 * (`primaryColor`/`secondaryColor`) o la pública (`colors[]`). Nunca fabrica colores ni foto.
 */
export const uniformVisual = (source, { photoFailed = false } = {}) => {
  const ordered = Array.isArray(source?.colors) ? source.colors : [source?.primaryColor, source?.secondaryColor];
  return {
    photoUrl: photoFailed ? null : source?.photoUrl ?? null,
    colors: (ordered || []).filter((color) => Boolean(color?.hex)),
  };
};

/**
 * La sección de color complementario arranca COLAPSADA en un alta y se abre sola al editar una opción que
 * ya tiene complementario (corrección 2026-09-14).
 */
export const complementaryStartsOpen = (option) => Boolean(option?.secondaryColor);

/** Quitar el complementario nunca toca el predominante (ni la foto, que es una acción aparte). */
export const clearComplementary = (form) => ({ ...form, secondaryColorId: "" });

/**
 * Guardar exige SOLO el color predominante: ni la foto ni el color complementario son obligatorios y no
 * hace falta abrir la sección complementaria ni desplazarse al final del modal. `busy` es la protección
 * contra doble envío mientras una petición (guardado o subida de foto) está en curso.
 */
export const canSubmitOptionForm = (form, { busy = false } = {}) =>
  !busy && Object.keys(validateOptionForm(form)).length === 0;

export const formFromOption = (option) => ({
  primaryColorId: option?.primaryColor?.id ? String(option.primaryColor.id) : "",
  secondaryColorId: option?.secondaryColor?.id ? String(option.secondaryColor.id) : "",
});

/** Ids de las opciones de una columna en su orden, moviendo una hacia arriba (-1) o abajo (+1). */
export const moveOptionInColumn = (column, idoption, direction) => {
  const ids = (column?.slots || []).filter((slot) => slot.kind === "OPTION").map((slot) => slot.option.id);
  const index = ids.indexOf(idoption);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) return null;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

// ------------------------------------- Plazo (fail closed) -------------------------------------
export const DEADLINE_REASON_TEXT = Object.freeze({
  NOT_CONFIGURED: "Configuración de plazo requerida — envío cerrado",
  EXPIRED: "Plazo vencido — envío cerrado",
  INVALID: "Plazo inválido — envío cerrado",
});

export const POLICY_SCOPE_LABELS = Object.freeze({
  EVENT: "Todo el evento",
  EVENT_SPORT: "Este deporte",
  EVENT_SPORT_INSTITUTION: "Extensión para una institución",
});

export const formatLimaDateTime = (limaLocal) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(limaLocal || ""));
  if (!match) return "";
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

export const describeDeadline = (deadline) => {
  if (!deadline) return { open: false, text: DEADLINE_REASON_TEXT.NOT_CONFIGURED };
  if (deadline.status === "OPEN") {
    return { open: true, text: `Abierto hasta ${formatLimaDateTime(deadline.closesAtLima)} (hora de Lima)` };
  }
  const reason = DEADLINE_REASON_TEXT[deadline.reason] || "Envío cerrado";
  const when = deadline.closesAtLima ? ` · cerró ${formatLimaDateTime(deadline.closesAtLima)}` : "";
  return { open: false, text: `${reason}${when}` };
};

/** "YYYY-MM-DDTHH:mm" de Lima para un instante UTC (ISO); "" si no es válido. */
export const utcToLimaLocal = (iso) => {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return "";
  return new Date(time - 5 * 60 * 60 * 1000).toISOString().slice(0, 16);
};

/** Instante UTC de un "YYYY-MM-DDTHH:mm" de Lima; null si no es válido. */
export const limaLocalToDate = (limaLocal) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(String(limaLocal || ""))) return null;
  const date = new Date(`${limaLocal}:00${LIMA_UTC_OFFSET}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Confirmación previa: una fecha en el pasado deja el envío cerrado apenas se guarda. */
export const isPastLimaDateTime = (limaLocal, now = new Date()) => {
  const date = limaLocalToDate(limaLocal);
  return Boolean(date && date.getTime() <= new Date(now).getTime());
};

export const TEAM_STATUS = Object.freeze({
  COMPLETE: { label: "Completo", tone: "emerald" },
  INCOMPLETE: { label: "Incompleto", tone: "amber" },
  MISSING: { label: "Sin registrar", tone: "rose" },
});

// --------------------------------------- Errores ---------------------------------------
const ERROR_MESSAGES = Object.freeze({
  UNIFORM_OPTIONS_DISABLED: "Las opciones de uniforme no están habilitadas.",
  UNIFORM_SLOTS_FULL: "Ya hay tres opciones para ese tipo y tono.",
  UNIFORM_SLOT_CONFLICT: "Otro usuario cambió este equipo al mismo tiempo. Recarga e intenta de nuevo.",
  DUPLICATE_COLOR: "El color complementario debe ser distinto del predominante.",
  INVALID_PRIMARY_COLOR: "Elige el color predominante del catálogo.",
  INVALID_SECONDARY_COLOR: "El color complementario debe ser del catálogo.",
  INVALID_UNIFORM_PHOTO: "La foto no es válida (JPEG, PNG o WebP, máximo 5 MB).",
  UNIFORM_PHOTO_STORAGE_NOT_CONFIGURED: "El almacenamiento de fotos no está configurado en este entorno.",
  INVALID_DESCRIPTION: "La descripción solo admite letras y espacios.",
  UNIFORM_TYPE_NOT_ALLOWED: "Ese tipo de uniforme no aplica a este deporte.",
  UNIFORM_OPTION_NOT_FOUND: "La opción ya no existe. Recarga la matriz.",
  REORDER_MISMATCH: "El orden cambió en otra sesión. Recarga e intenta de nuevo.",
  UNIFORM_SUBMISSION_CLOSED: "El envío de uniformes está cerrado.",
  REASON_REQUIRED: "La extensión para una institución requiere un motivo.",
  EXTENSION_NOT_LATER: "La extensión debe ser posterior a la fecha límite vigente del deporte.",
  INVALID_DEADLINE: "Ingresa fecha, hora y minuto válidos (hora de Lima).",
  POLICY_CONFLICT: "Otro administrador guardó este plazo al mismo tiempo. Recarga e intenta de nuevo.",
  UNIFORM_OPTION_NOT_FOR_TEAM: "La opción elegida no pertenece al equipo de ese lado.",
  UNIFORM_KIND_NOT_ALLOWED: "Ese uniforme no aplica a este deporte.",
  UNIFORM_TEAM_NOT_FOUND: "Ese lado del partido no tiene un equipo con uniformes registrados.",
  UNIFORM_LEGACY_READ_ONLY: "Los uniformes se gestionan en Administración General → Uniformes.",
  EVENT_ACCESS_DENIED: "No tienes acceso a este evento.",
  // FTR-0041: asignación inteligente de camisetas.
  UNIFORM_PLAN_CHANGED: "La programación cambió desde la vista previa. Se recalculó la propuesta; revísala antes de aplicar.",
  CONFIRMATION_REQUIRED: "La propuesta tiene advertencias fuertes que debes confirmar antes de guardar.",
  SCOPE_TOO_LARGE: "Hay demasiados partidos en una sola operación. Selecciona partidos.",
  INVALID_MATCH_SELECTION: "Selecciona al menos un partido elegible.",
  // FTR-0044: recalculo selectivo y versiones de uniformes.
  INVALID_UNIFORM_KINDS: "Elige al menos camisetas principales o alternas.",
  VERSIONING_UNAVAILABLE: "El versionamiento de uniformes todavía no está disponible en este entorno.",
  VERSION_CONFLICT: "Otra persona guardó una versión al mismo tiempo. Vuelve a intentarlo.",
});

export const getUniformErrorMessage = (error, fallback = "No se pudo completar la operación.") => {
  const data = error?.response?.data || {};
  return ERROR_MESSAGES[data.code] || data.message || data.msg || fallback;
};

// ------------------------------------- Programación -------------------------------------
export const KIND_LABELS = Object.freeze({ MAIN: "Uniforme", EXCLUSIVE: { 2: "Uniforme Arquero", 3: "Uniforme Líbero" } });
export const kindLabel = ({ kind, idtype }) => (kind === "MAIN" ? KIND_LABELS.MAIN : KIND_LABELS.EXCLUSIVE[idtype] || "Uniforme exclusivo");

/** Selección inicial por lado y clase: la opción enlazada vigente (no obsoleta) o vacío. */
export const initialSelections = (context) => {
  const selections = {};
  for (const side of context?.sides || []) {
    for (const { kind } of context?.kinds || []) {
      const current = side.current?.[kind];
      selections[`${side.side}:${kind}`] = current?.option && !current.stale ? current.option.id : null;
    }
  }
  return selections;
};

/** Asignaciones a enviar: solo claves cambiadas; quitar solo si había un enlace. */
export const changedAssignments = (initial, selections) =>
  Object.keys(selections)
    .filter((key) => (selections[key] ?? null) !== (initial[key] ?? null))
    .map((key) => {
      const [side, kind] = key.split(":");
      return { side: Number(side), kind, idoption: selections[key] ?? null };
    });
